import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { Spinner } from './spinner'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-3 whitespace-nowrap rounded-full text-sm font-medium transition-all duration-200 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 cursor-pointer",
  {
    variants: {
      variant: {
        default:
          'bg-button-primary-default text-button-primary-text hover:shadow-[0px_2px_4px_0px_rgba(0,0,0,0.15)] active:shadow-[0px_4px_8px_0px_rgba(0,0,0,0.2)] disabled:bg-button-primary-default/40 disabled:text-button-primary-disabled-text',
        secondary:
          'bg-white font-normal border border-gray-300 hover:bg-gray-50 focus-visible:ring-gray-500/20 hover:shadow-md disabled:bg-[#F7F8F9] disabled:text-text-disabled',
        destructive: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500/20 shadow-sm hover:shadow-md',
        outline: 'border border-gray-300 bg-white hover:bg-gray-50 focus-visible:ring-gray-500/20 shadow-sm',
        ghost: 'text-text-secondary hover:bg-background-hover focus-visible:ring-gray-500/20',
        link: 'text-text-link underline-offset-4 hover:underline focus-visible:ring-blue-500/20 !p-0 !h-fit disabled:text-text-disabled disabled:cursor-not-allowed',
      },
      size: {
        default: 'h-9 px-5 md:px-6 py-3 text-sm',
        sm: 'h-8 px-4 py-2 text-xs',
        lg: 'h-12 px-8 py-4 text-base',
        xl: 'h-12 px-10 py-4 text-lg',
        icon: 'size-fit p-1.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)
const loadingVariants = cva('', {
  variants: {
    variant: {
      default: 'text-text-primary',
      secondary: 'text-text-secondary',
      destructive: 'text-white',
      outline: 'text-text-primary',
      ghost: 'text-text-secondary',
      link: 'text-text-link',
    },
    size: {
      default: 'size-4',
      sm: 'size-3',
      lg: 'size-5',
      xl: 'size-8',
      icon: 'size-5',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
})
export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    loading?: boolean
    icon?: React.ReactNode
    trailingIcon?: React.ReactNode
    href?: string
  }
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, loading = false, icon, trailingIcon, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    if (asChild) {
      return (
        <Comp ref={ref} data-slot='button' className={cn(buttonVariants({ variant, size, className }))} {...props}>
          {children}
        </Comp>
      )
    }

    return (
      <Comp ref={ref} data-slot='button' className={cn(buttonVariants({ variant, size, className }))} {...props}>
        {loading && <Spinner className={loadingVariants({ variant, size })} />}
        {icon && !loading && icon}
        {children}
        {trailingIcon && !loading && trailingIcon}
      </Comp>
    )
  }
)

Button.displayName = 'Button'

export { Button, buttonVariants }
